import React, { useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import PerformanceSideBar from './PerformanceSideBar';
import usePerformance from './usePerformance';
import DashboardSelectorMenu from './views/CustomReport/DashboardSelectorMenu';
import { onAddReport, onDashboardTypeSelect } from './Performance.service';
import { useSelector } from 'react-redux';
import { isPmToolJiraSelector } from 'state/selectors/sensors-authorizations';
import { useHistory, useRouteMatch } from 'react-router';
import withErrorBoundary from 'components/HOC/ErrorBoundryHOC';
import {
  FullHeightGrid,
  StyledBackground,
  StyledMetricsContainer
} from './Performance.style';
import PermissionsHOC from 'connected-components/HOC/PermissionsHOC';
import { CAN_EDIT_ACCOUNT_TYPES, TEAM_TYPE_MAP } from '@linearb/linjs-common';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import AllContributorsBanner from '../dashboard/doraV2/Widgets/AllContributorsBanner';

const Performance = ({
  history,
  match,
  disableTeamPicker,
  setDisableTeamPicker
}) => {
  const [addDashboardAnchorEl, setAddDashboardAnchorEl] = useState(null);
  const {
    loaded,
    newCustomDashBoard,
    selectedReport,
    onSelectReport,
    setNewCustomDashBoard,
    generateNewReport,
    setSelectedReport,
    maxDashboards,
    newFormattedBoard,
    reports,
    customDashboardsFiltered,
    pinnedDashboards,
    sideBarDashboards,
    canCreateMoreCustomDashboards,
    isCustomPmMetricsEnabled,
    shouldHideProjectManagement
  } = usePerformance({ disableTeamPicker, setDisableTeamPicker });

  const isJiraProvider = useSelector(isPmToolJiraSelector);
  const selectedTeam = useSelector(selectedTeamSelector);
  const isAllContributorsSelected =
    selectedTeam?.type === TEAM_TYPE_MAP.ALL_CONTRIBUTORS;

  return (
    <>
      <StyledBackground>
        {isAllContributorsSelected && <AllContributorsBanner />}
        <FullHeightGrid>
          <PerformanceSideBar
            loaded={loaded}
            reports={reports}
            newCustomDashBoard={newCustomDashBoard}
            customDashboards={sideBarDashboards}
            selectedReport={selectedReport}
            onSelectReport={onSelectReport}
            shouldHideProjectManagement={shouldHideProjectManagement}
            onAddReport={event =>
              onAddReport({
                event,
                isCustomPmMetricsEnabled,
                setAddDashboardAnchorEl,
                generateNewReport,
                setNewCustomDashBoard,
                setSelectedReport,
                history,
                match,
                isJiraProvider
              })
            }
            maxDashboards={maxDashboards}
            pinnedDashboards={pinnedDashboards}
            canCreateMoreCustomDashboards={canCreateMoreCustomDashboards}
          />

          <StyledMetricsContainer>
            <Switch>
              {reports.map((report, idx) => (
                <Route
                  path={`${match.path}/${report.route}`}
                  exact
                  render={report.component}
                  key={`${idx}-default`}
                />
              ))}
              {[
                ...customDashboardsFiltered,
                ...(newFormattedBoard ? [newFormattedBoard] : [])
              ].map((dashboard, idx) => (
                <Route
                  path={`${match.path}/${dashboard.route}`}
                  exact
                  render={dashboard.component}
                  key={`${idx}-custom`}
                />
              ))}
            </Switch>
          </StyledMetricsContainer>
        </FullHeightGrid>
      </StyledBackground>
      {PermissionsHOC({
        WrappedComponent: (
          <DashboardSelectorMenu
            anchorEl={addDashboardAnchorEl}
            onSelect={internalType =>
              onDashboardTypeSelect({
                internalType,
                setAddDashboardAnchorEl,
                generateNewReport,
                history,
                match,
                setNewCustomDashBoard,
                setSelectedReport
              })
            }
            setAnchorEl={setAddDashboardAnchorEl}
          />
        ),
        requiredPermissions: CAN_EDIT_ACCOUNT_TYPES
      })}
    </>
  );
};

const isRouterPropsEqual = (newProps, prevProps) =>
  isEqual(newProps.match, prevProps.match) &&
  isEqual(newProps.history, prevProps.history);

const MemoPerformance = React.memo(Performance, isRouterPropsEqual);

const RoutedPerformance = props => {
  const match = useRouteMatch();
  const history = useHistory();
  return <MemoPerformance {...props} match={match} history={history} />;
};

export default withErrorBoundary(RoutedPerformance, 'Metrics');
